import React, { useState, useEffect } from 'react';
import { Menu, Typography, Tag } from 'antd';
import { AppstoreOutlined } from '@ant-design/icons';
import { Category } from '../../types/products';

const { Title, Text } = Typography;

interface CategorySelectorProps {
  categories: Category[];
  selectedCategoryId: string | null;
  onCategoryChange: (categoryId: string | null) => void;
  loading?: boolean;
  compact?: boolean; // For mobile view
  horizontalScroll?: boolean; // Single row with horizontal scroll
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
  categories,
  selectedCategoryId,
  onCategoryChange,
  loading = false,
  compact = false,
  horizontalScroll = false,
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Mobile compact view - Show categories as chips
  if (compact || isMobile) {
    return (
      <div>
        <Text strong style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', display: 'block' }}>
          Categorías
        </Text>
        <div
          className={horizontalScroll ? 'category-scroll-row' : undefined}
          style={horizontalScroll ? {
            display: 'flex',
            flexWrap: 'nowrap',
            gap: '6px',
            overflowX: 'auto',
            overflowY: 'hidden',
            paddingBottom: '6px',
            scrollbarWidth: 'thin',
          } : {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            maxHeight: '120px',
            overflowY: 'auto',
            paddingBottom: '4px'
          }}>
          {/* "Todos" chip */}
          <Tag
            color={selectedCategoryId === null ? 'blue' : 'default'}
            style={{
              cursor: 'pointer',
              fontSize: '12px',
              padding: '4px 12px',
              margin: 0,
              borderRadius: '16px',
              border: selectedCategoryId === null ? '1px solid #1890ff' : '1px solid var(--border-color-strong)',
              fontWeight: selectedCategoryId === null ? 'bold' : 'normal',
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
            onClick={() => onCategoryChange(null)}
          >
            <AppstoreOutlined /> Todos
          </Tag>

          {/* Category chips */}
          {categories.map(category => (
            <Tag
              key={category.id}
              color={selectedCategoryId === category.id ? 'blue' : 'default'}
              style={{
                cursor: 'pointer',
                fontSize: '12px',
                padding: '4px 12px',
                margin: 0,
                borderRadius: '16px',
                border: selectedCategoryId === category.id ? '1px solid #1890ff' : '1px solid var(--border-color-strong)',
                fontWeight: selectedCategoryId === category.id ? 'bold' : 'normal',
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
              onClick={() => onCategoryChange(category.id)}
            >
              {category.name} ({category.productCount})
            </Tag>
          ))}
        </div>
      </div>
    );
  }

  // Desktop view - Original horizontal menu
  const menuItems = [
    {
      key: 'all',
      icon: <AppstoreOutlined />,
      label: 'Todos los productos',
    },
    ...categories.map(category => ({
      key: category.id,
      label: `${category.name} (${category.productCount})`,
    })),
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    onCategoryChange(key === 'all' ? null : key);
  };

  return (
    <div style={{ marginBottom: 8 }}>
      <Title level={5} style={{ marginBottom: 6, marginTop: 0 }}>
        Categorías
      </Title>

      <Menu
        mode="horizontal"
        selectedKeys={[selectedCategoryId || 'all']}
        items={menuItems}
        onClick={handleMenuClick}
        style={{
          borderBottom: 'none',
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: 8,
          padding: '4px',
        }}
      />
    </div>
  );
};

export default CategorySelector;